import { useCallback, useMemo } from 'react';

/**
 * Hook pour persister le playerId d'un user dans une session donnee.
 *
 * Cle localStorage : `ember.session.<shortCode>.playerId`
 *
 * Permet de :
 *   - Eviter qu'un user qui reload sa page cree un 2e SessionPlayer
 *   - Retrouver son playerId pour soumettre les reponses des rounds
 *   - Distinguer "je suis player de cette partie" vs "je suis un visiteur"
 *
 * Pas de mecanisme d'auth fort : si quelqu'un connait le shortCode et
 * efface son localStorage, il peut creer un nouveau player. Pour le MVP,
 * c'est acceptable (jeu de confiance, pas de donnees sensibles cote user).
 */
export function useSessionPlayer(shortCode: string | undefined) {
  const key = useMemo(
    () => (shortCode ? `ember.session.${shortCode}.playerId` : null),
    [shortCode],
  );

  const playerId = useMemo<string | null>(() => {
    if (!key || typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  }, [key]);

  const setPlayerId = useCallback(
    (id: string) => {
      if (!key || typeof window === 'undefined') return;
      window.localStorage.setItem(key, id);
    },
    [key],
  );

  const clearPlayerId = useCallback(() => {
    if (!key || typeof window === 'undefined') return;
    window.localStorage.removeItem(key);
  }, [key]);

  return { playerId, setPlayerId, clearPlayerId };
}
